import { IProvided } from '@/hooks/useEventGetters';
import UserAvatar from '@/components/ui/userAvatar';
import { AuthContext, ConferenceContext } from '@/store';
import { markdownMini } from '@/lib/utils/DataFormat';
import { IEvent } from '@/types/event';
import {
  Box,
  Button,
  ButtonBase,
  CardContent,
  CardHeader,
  Grid,
  Icon,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import React, { FC, useContext } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AspectRatio } from '@/components/aspect-ratio/aspect-ratio';
import { ConditionalRenderer } from '@/components/conditional-renderer';

interface IProps extends IProvided {
  event: IEvent;
  nrOfRooms: number;
  hideProgramSubHeader: boolean;
  hideFavoriteButton: boolean;
  children?: React.ReactNode;
}

const useStyles = (theme: any) => ({
  title: {
    color: `${theme.palette.text.primary} !important`,
    fontSize: `${theme.typography.subtitle1.fontSize} !important`,
    lineHeight: '0px !important',
    '& > a': {
      color: 'inherit !important',
    },
  },
  liveText: {
    color: `${theme.palette.error.main} !important`,
    lineHeight: `0px !important`,
  },
  subheader: {
    color: `${theme.palette.primary.main} !important`,
    fontSize: `${theme.typography.subtitle2.fontSize} !important`,
    '& > a': {
      color: 'inherit !important',
    },
  },
  contentRoot: {
    '& > div': {
      paddingTop: `0px !important`,
      paddingBottom: `${theme.spacing(1)} !important`,
    },
    '& > div:last-child': {
      paddingBottom: `${theme.spacing(2)} !important`,
    },
  },
  replay: {
    marginBottom: `${theme.spacing(1)} !important`,
    borderRadius: `5px !important`,
    backgroundImage: 'none !important',
    backgroundSize: 'cover !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: 'center center !important',
    backgroundColor: 'rgba(0,0,0,1) !important',
    opacity: '1 !important',
    '& > div': {
      cursor: 'pointer !important',
      color: '#ffffff !important',
      backgroundColor: 'rgba(0,0,0,.5) !important',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,.1) !important',
      },
    },
  },
  replayContent: {
    borderRadius: '5px !important',
    flexDirection: 'row !important',
  },
  visitor: {
    marginRight: `${theme.spacing(1)} !important`,
  },
  forum: {
    '& a': {
      color: `${theme.palette.grey[500]} !important`,
    },
  },
  description: {
    whiteSpace: 'pre-wrap !important',
  },
  buttonHolder: {
    paddingLeft: `${theme.spacing(2)} !important`,
  },
  favoriteButton: {
    float: 'right !important',
  },
});

export const EventListContent: FC<IProps> = ({
  event,
  eventStatus,
  eventUrl,
  eventAssets,
  eventAccess,
  eventVisitors,
  eventRoomTitle,
  children,
  nrOfRooms,
  hideProgramSubHeader,
  hideFavoriteButton = false,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { authState } = useContext(AuthContext);
  const { openUserPopup, toggleFavorite } = useContext(ConferenceContext);

  const navigate = useNavigate();

  const triggerToggleFavorite = (viewEvent: IEvent) => {
    toggleFavorite(
      viewEvent.id,
      viewEvent.favorites.length === 0
    ).then(() => {});
  };

  const programHeader = (
    <Typography component="span" variant="subtitle1" sx={classes.title}>
      <ConditionalRenderer
        condition={eventStatus === 'passed' && eventAccess}
        render={(children) => <RouterLink to={eventUrl}>{children}</RouterLink>}
      >
        {eventStatus === 'live' && (
          <Box component="span" sx={classes.liveText}>
            LIVE{' '}
          </Box>
        )}
        {event.title}
      </ConditionalRenderer>
    </Typography>
  );

  const programSubHeader = (
    <ConditionalRenderer
      condition={eventStatus !== 'passed'}
      render={(children) => <RouterLink to={eventUrl}>{children}</RouterLink>}
    >
      <strong>{eventRoomTitle}</strong>
    </ConditionalRenderer>
  );

  const replay = eventAccess && (
    <Grid size={{ xs: 12, md: 2 }} onClick={() => navigate(eventUrl)}>
      <AspectRatio
        styles={{
          aspectContainer: classes.replay as any,
          aspectContent: classes.replayContent as any,
        }}
      >
        <Icon>play_arrow</Icon>
        <strong>Replay</strong>
      </AspectRatio>
    </Grid>
  );

  return (
    <Grid container>
      <Grid
        size={{ xs: 12, md: eventAccess ? 10 : 12 }}
        sx={classes.contentRoot}
      >
        {!hideFavoriteButton && event.favorites && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => triggerToggleFavorite(event)}
            sx={classes.favoriteButton}
          >
            <Icon>{event?.favorites?.length > 0 ? 'star' : 'star_border'}</Icon>
          </IconButton>
        )}

        <CardHeader
          title={programHeader}
          subheader={
            nrOfRooms > 1 &&
            hideProgramSubHeader !== true &&
            !(window as any).SETTINGS.HIDE_PROGRAM_ROOMS_IN_MENU
              ? programSubHeader
              : null
          }
          slotProps={{
            title: { sx: classes.title },
            subheader: { sx: classes.subheader },
          }}
        />

        {event.description && (
          <CardContent>
            <Typography
              component="p"
              variant="body1"
              sx={classes.description}
              dangerouslySetInnerHTML={markdownMini(event.description)}
            ></Typography>
          </CardContent>
        )}
        {eventVisitors.length > 0 && (
          <CardContent>
            {eventVisitors.map((visitor) => (
              <ButtonBase
                key={visitor.id}
                sx={classes.visitor}
                disabled={!authState.userLoggedIn}
                onClick={() => openUserPopup(visitor)}
              >
                <UserAvatar size="medium" user={visitor} />
              </ButtonBase>
            ))}
          </CardContent>
        )}
        {event.booking_type === 'entity' &&
          event.entityListId &&
          event.booking_settings && (
            <Box sx={classes.buttonHolder}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icon>handshake</Icon>}
                href={`/live/list/${event.entityListId}`}
              >
                {event.booking_settings.button}
              </Button>
            </Box>
          )}
        {children && <CardContent>{children}</CardContent>}
      </Grid>
      {replay}
    </Grid>
  );
};

export const EventListLiveContent: FC<IProps> = ({
  event,
  eventAssets,
  eventAccess,
  eventVisitors,
  children,
}) => {
  const classes = useStyles({
    eventAssets,
  });
  const { authState } = useContext(AuthContext);
  const { openUserPopup } = useContext(ConferenceContext);

  const programHeader = (
    <Typography component="span" variant="subtitle1" sx={classes.title}>
      {event.title}
    </Typography>
  );

  return (
    <Grid container>
      <Grid
        size={{ xs: 12, md: eventAccess ? 10 : 12 }}
        sx={classes.contentRoot}
      >
        <CardHeader
          title={programHeader}
          slotProps={{
            title: { sx: classes.title },
          }}
        />
        {event.description && (
          <CardContent>
            <Typography
              component="p"
              variant="body1"
              sx={classes.description}
              dangerouslySetInnerHTML={markdownMini(event.description)}
            ></Typography>
          </CardContent>
        )}
        {eventVisitors.length > 0 && (
          <CardContent>
            {eventVisitors.map((visitor) => (
              <ButtonBase
                key={visitor.id}
                sx={classes.visitor}
                onClick={() => openUserPopup(visitor)}
                disabled={!authState.userLoggedIn}
              >
                <UserAvatar size="medium" user={visitor} />
              </ButtonBase>
            ))}
          </CardContent>
        )}
        {event.booking_type === 'entity' &&
          event.entityListId &&
          event.booking_settings && (
            <Box sx={classes.buttonHolder}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icon>handshake</Icon>}
                href={`/live/list/${event.entityListId}`}
              >
                {event.booking_settings.button}
              </Button>
            </Box>
          )}
        {children && <CardContent>{children}</CardContent>}
      </Grid>
    </Grid>
  );
};
